import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { toast } from 'vue-sonner'
import DashboardLayout from '@/app/layouts/DashboardLayout.vue'
import type { Permission } from '@/core/auth/permissions'
import { env } from '@/core/config/env'
import { useAuthStore } from '@/stores/auth.store'

declare module 'vue-router' {
  interface RouteMeta {
    title?: string
    public?: boolean
    requiresAuth?: boolean
    superadminOnly?: boolean
    permission?: Permission
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    alias: ['/home', '/landing'],
    name: 'landing',
    component: () => import('@/features/landing/pages/LandingPage.vue'),
    meta: { title: 'Minto POS - Solusi Kasir Online & POS Modern UMKM', public: true },
  },
  {
    path: '/privacy-policy',
    alias: ['/privacy', '/kebijakan-privasi'],
    name: 'privacy-policy',
    component: () => import('@/features/landing/pages/PrivacyPolicyPage.vue'),
    meta: {
      title: 'Kebijakan Privasi - Minto POS (Solusi Kasir Online UMKM)',
      public: true,
    },
  },
  {
    path: '/terms',
    alias: ['/terms-of-service', '/syarat-ketentuan'],
    name: 'terms-of-service',
    component: () => import('@/features/landing/pages/TermsOfServicePage.vue'),
    meta: {
      title: 'Syarat & Ketentuan Layanan - Minto POS',
      public: true,
    },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/features/auth/pages/LoginPage.vue'),
    meta: { title: 'Masuk', public: true },
  },
  {
    path: '/order',
    name: 'public-order',
    component: () => import('@/features/order/pages/PublicOrderPage.vue'),
    meta: { title: 'Pesan Menu (Self-Order)', public: true },
  },
  {
    path: '/scan',
    name: 'public-scan',
    component: () => import('@/features/order/pages/PublicOrderPage.vue'),
    meta: { title: 'Pesan Menu (Self-Order)', public: true },
  },
  {
    path: '/',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/features/dashboard/pages/DashboardPage.vue'),
        meta: { title: 'Dashboard' },
      },
      {
        path: 'placeholder/:section',
        name: 'placeholder',
        component: () => import('@/app/pages/ComingSoonPage.vue'),
        meta: { title: 'Segera Hadir' },
      },
      {
        path: 'transaksi/kasir',
        name: 'cashier',
        component: () => import('@/features/cashier/pages/CashierPage.vue'),
        meta: { title: 'Kasir', permission: 'transaction.create' },
      },
      {
        path: 'users-access/accounts',
        name: 'accounts',
        component: () => import('@/features/access/pages/AccountsPage.vue'),
        meta: { title: 'Akun', permission: 'user.view' },
      },
      {
        path: 'users-access/roles',
        name: 'roles',
        component: () => import('@/features/access/pages/RolesPage.vue'),
        meta: { title: 'Peran', permission: 'role.view' },
      },
      {
        path: 'produk/list',
        name: 'catalog-products',
        component: () => import('@/features/catalog/pages/ProductsPage.vue'),
        meta: { title: 'Produk', permission: 'product.view' },
      },
      {
        path: 'produk/kategori',
        name: 'catalog-categories',
        component: () => import('@/features/catalog/pages/CategoriesPage.vue'),
        meta: { title: 'Kategori', permission: 'product.view' },
      },
      {
        path: 'produk/variant',
        name: 'catalog-variants',
        component: () => import('@/features/catalog/pages/ModifierPage.vue'),
        props: { kind: 'variants' },
        meta: { title: 'Varian', permission: 'product.view' },
      },
      {
        path: 'produk/addon',
        name: 'catalog-addons',
        component: () => import('@/features/catalog/pages/ModifierPage.vue'),
        props: { kind: 'addons' },
        meta: { title: 'Add-on', permission: 'product.view' },
      },
      {
        path: 'produk/grup-addon',
        name: 'catalog-addon-groups',
        component: () => import('@/features/catalog/pages/AddonGroupsPage.vue'),
        meta: { title: 'Grup Add-on', permission: 'product.view' },
      },
      {
        path: 'produk/tipe-harga',
        name: 'catalog-price-types',
        component: () => import('@/features/catalog/pages/PriceTypesPage.vue'),
        meta: { title: 'Tipe Harga', permission: 'product.view' },
      },
      {
        path: 'pembayaran/metode',
        name: 'payment-methods',
        component: () => import('@/features/payment/pages/PaymentMethodsPage.vue'),
        meta: { title: 'Metode Pembayaran', permission: 'payment.view' },
      },
      {
        path: 'pembayaran/diskon',
        name: 'discounts',
        component: () => import('@/features/discount/pages/DiscountsPage.vue'),
        meta: { title: 'Diskon', permission: 'discount.view' },
      },
      {
        path: 'inventory/satuan',
        name: 'inventory-units',
        component: () => import('@/features/inventory/pages/UnitsPage.vue'),
        meta: { title: 'Satuan', permission: 'inventory.view' },
      },
      {
        path: 'inventory/bahan',
        name: 'inventory-ingredients',
        component: () => import('@/features/inventory/pages/IngredientsPage.vue'),
        meta: { title: 'Bahan Baku', permission: 'inventory.view' },
      },
      {
        path: 'inventory/resep',
        name: 'inventory-recipes',
        component: () => import('@/features/inventory/pages/RecipesPage.vue'),
        meta: { title: 'Resep', permission: 'inventory.view' },
      },
      {
        path: 'inventory/productions',
        name: 'inventory-productions',
        component: () => import('@/features/inventory/pages/ProductionsPage.vue'),
        meta: { title: 'Produksi', permission: 'inventory.view' },
      },
      {
        path: 'inventory/purchases',
        name: 'inventory-purchases',
        component: () => import('@/features/inventory/pages/PurchasesPage.vue'),
        meta: { title: 'Pembelian', permission: 'inventory.view' },
      },
      {
        path: 'inventory/transfers',
        name: 'inventory-transfers',
        component: () => import('@/features/inventory/pages/StockTransfersPage.vue'),
        meta: { title: 'Transfer Antar Outlet', permission: 'stock.transfer' },
      },
      {
        path: 'stock/adjustments',
        name: 'stock-adjustments',
        component: () => import('@/features/inventory/pages/StockAdjustmentsPage.vue'),
        meta: { title: 'Penyesuaian Stok', permission: 'stock.adjust' },
      },
      {
        path: 'stock/card',
        name: 'stock-card',
        component: () => import('@/features/inventory/pages/StockCardPage.vue'),
        meta: { title: 'Kartu Stok', permission: 'inventory.view' },
      },
      {
        path: 'reports/stock',
        name: 'reports-stock',
        component: () => import('@/features/inventory/pages/StockReportPage.vue'),
        meta: { title: 'Laporan Stok', permission: 'inventory.view' },
      },
      {
        path: 'reports/transactions',
        name: 'reports-transactions',
        component: () => import('@/features/reports/pages/TransactionReportPage.vue'),
        meta: { title: 'Laporan Transaksi', permission: 'report.transaction.view' as Permission },
      },
      {
        path: 'reports/gross-profit',
        name: 'reports-gross-profit',
        component: () => import('@/features/reports/pages/GrossProfitReportPage.vue'),
        meta: { title: 'Laporan Laba Kotor & Margin', permission: 'inventory.view' },
      },
      {
        path: 'reports/shifts',
        name: 'reports-shifts',
        component: () => import('@/features/cashier/pages/CashierSessionsPage.vue'),
        meta: { title: 'Laporan Shift Kasir', permission: 'cashier.report' },
      },
      {
        path: 'reports/central-kitchen',
        name: 'reports-central-kitchen',
        component: () => import('@/features/reports/pages/CentralKitchenReportPage.vue'),
        meta: { title: 'Laporan Central Kitchen & PO', permission: 'report.central_kitchen.view' },
      },
      {
        path: 'pengaturan/outlet',
        name: 'outlet-settings',
        component: () => import('@/features/access/pages/OutletSettingsPage.vue'),
        meta: { title: 'Pengaturan Outlet' },
      },
      {
        path: 'pengaturan/printer',
        name: 'printer-settings',
        component: () => import('@/features/access/pages/PrinterSettingsPage.vue'),
        meta: { title: 'Pengaturan Printer' },
      },
      {
        path: 'superadmin/tenants',
        name: 'superadmin-tenants',
        component: () => import('@/features/superadmin/pages/TenantsPage.vue'),
        meta: { title: 'Kelola Tenant', superadminOnly: true },
      },
      {
        path: 'superadmin/tenants/:id',
        name: 'superadmin-tenant-detail',
        component: () => import('@/features/superadmin/pages/TenantDetailPage.vue'),
        meta: { title: 'Detail Tenant', superadminOnly: true },
      },
      {
        path: 'superadmin/subscribers',
        name: 'superadmin-subscribers',
        component: () => import('@/features/superadmin/pages/SubscribersPage.vue'),
        meta: { title: 'Subscriber Outlet', superadminOnly: true },
      },
      {
        path: 'superadmin/restore-backup',
        name: 'superadmin-restore-backup',
        component: () => import('@/features/transaction/pages/RestoreBackupPage.vue'),
        meta: { title: 'Restore Backup Offline', superadminOnly: true },
      },
      {
        path: 'transaksi/restore-backup',
        redirect: '/superadmin/restore-backup',
      },

      // Flag dicek sebelum permission: dengan flag mati, route ini tidak ada
      // untuk siapa pun termasuk superadmin, karena masalahnya bukan izin
      // melainkan endpoint /tables yang belum tersedia di BE.
      ...(env.featureTableLayout
        ? [
            {
              path: 'pengaturan/layout-meja',
              name: 'table-layout',
              component: () => import('@/features/table/pages/TableLayoutPage.vue'),
              meta: {
                title: 'Layout Meja',
                // Lihat catatan di navigation.ts: izin ini belum diterbitkan BE.
                permission: env.useMockTables ? undefined : ('table.view' as Permission),
              },
            },
          ]
        : []),
    ],
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
]

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior: (to, _from, savedPosition) => {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    }
    return { top: 0, behavior: 'smooth' }
  },
})

/** Guard auth: pulihkan sesi dari token, lalu proteksi route. */
router.beforeEach(async (to) => {
  if (to.meta.title) {
    document.title = to.meta.title
  }

  const auth = useAuthStore()

  // Ada token tapi profil belum dimuat (mis. baru refresh halaman) → coba pulihkan.
  if (auth.hasToken && !auth.isAuthenticated) {
    await auth.bootstrap()
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  // Sudah login tapi membuka /login → arahkan ke dashboard.
  if (to.name === 'login' && auth.isAuthenticated) {
    return { path: '/dashboard' }
  }

  // Khusus Superadmin: blokir jika bukan superadmin
  if (to.meta.superadminOnly && !auth.user?.isSuperadmin) {
    toast.error('Halaman ini khusus untuk Superadmin.')
    return { path: '/dashboard' }
  }

  // RBAC: blokir route yang butuh izin tertentu (superadmin selalu lolos).
  if (to.meta.permission && !auth.can(to.meta.permission)) {
    toast.error('Anda tidak punya izin untuk mengakses halaman itu.')
    return { path: '/dashboard' }
  }

  return true
})

/**
 * Cleanup pointer-events pada body setelah navigasi.
 * Mencegah layar terkunci (tidak bisa diklik) jika ada dialog/sheet/dropdown
 * dari Reka UI yang gagal membersihkan pointer-events saat route berganti.
 */
router.afterEach(() => {
  if (typeof document !== 'undefined' && document.body.style.pointerEvents === 'none') {
    document.body.style.removeProperty('pointer-events')
  }
})

/**
 * Tangani ChunkLoadError / Dynamic Import failure.
 * Terjadi jika ada rilis baru dan browser mencoba memuat chunk lama yang sudah
 * tidak ada (404). Reload halaman akan otomatis mengambil index.html & chunk terbaru.
 */
router.onError((error, to) => {
  const isChunkLoadFailed =
    error?.message?.includes('Failed to fetch dynamically imported module') ||
    error?.message?.includes('Importing a module script failed') ||
    error?.message?.includes('error loading dynamically imported module')

  if (isChunkLoadFailed && to?.fullPath) {
    window.location.href = to.fullPath
  }
})
